// client/src/pages/PostTask.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

/**
 * Fully Fixed PostTask Page:
 * ✔ Prevents jwt malformed
 * ✔ Upload happens ONLY after login
 * ✔ Saves draft
 * ✔ Restores draft after login
 * ✔ Redirect-after-login preserved
 * ✔ No duplicate submit
 */

export default function PostTask() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Load draft if exists
  const savedDraft = JSON.parse(localStorage.getItem("cp_draft_post_task") || "{}");

  const [form, setForm] = useState({
    title: savedDraft.title || "",
    description: savedDraft.description || "",
    category: savedDraft.category || "",
    budgetMin: savedDraft.budgetMin || "",
    budgetMax: savedDraft.budgetMax || "",
    dueDate: savedDraft.dueDate || "",
    imageFile: null,
  });

  const [preview, setPreview] = useState(savedDraft.preview || null);
  const [loading, setLoading] = useState(false);

  // 🔥 Auto-save draft every time form changes
  useEffect(() => {
    localStorage.setItem(
      "cp_draft_post_task",
      JSON.stringify({
        ...form,
        preview,
      })
    );
  }, [form, preview]);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setForm((p) => ({ ...p, imageFile: f }));
    setPreview(URL.createObjectURL(f));
  };

  // 🔥 Upload image only AFTER login
  const uploadImage = async () => {
    if (!user) return ""; // <-- important fix
    if (!form.imageFile) return "";

    try {
      const token = localStorage.getItem("cp_token");
      if (!token) return "";

      const fd = new FormData();
      fd.append("image", form.imageFile);

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });

      const d = await res.json();
      if (!res.ok) {
        console.log("UPLOAD FAILED:", d);
        return "";
      }

      return d.url;
    } catch (err) {
      console.error("UPLOAD ERROR:", err);
      return "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ❌ Prevent duplicate submissions
    if (loading) return;

    // 🔥 STOP EARLY if user not logged in
    if (!user) {
      localStorage.setItem("cp_redirect_after_login", "/post-task");
      alert("Please login first!");
      return navigate("/login"); // stop everything
    }

    const { title, description, category, budgetMin, budgetMax, dueDate } = form;

    if (
      !title.trim() ||
      !description.trim() ||
      !category ||
      !budgetMin ||
      !budgetMax ||
      !dueDate
    ) {
      return alert("Please fill all required fields!");
    }

    setLoading(true);

    try {
      const imageUrl = await uploadImage();

      const token = localStorage.getItem("cp_token");
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/tasks`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim(),
          category,
          budgetMin,
          budgetMax,
          dueDate,
          imageUrl,
        }),
      });

      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Error posting task");

      // SUCCESS
      const newTask = d.task;

      // clear draft
      localStorage.removeItem("cp_draft_post_task");

      alert("Task posted successfully!");

      navigate(`/task/${newTask._id}`);
    } catch (err) {
      console.error(err);
      alert(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-8">
      <div className="max-w-3xl mx-auto bg-white dark:bg-slate-800 rounded-xl shadow p-8">
        <h1 className="text-2xl font-bold mb-4 text-slate-900 dark:text-white">
          Create a new Task
        </h1>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
              Title
            </label>
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="mt-1 w-full border p-2 rounded bg-white dark:bg-slate-700"
              placeholder="Task title"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
              Description
            </label>
            <textarea
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              rows={5}
              className="mt-1 w-full border p-2 rounded bg-white dark:bg-slate-700"
              placeholder="Describe what you need"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium dark:text-slate-200">
                Category
              </label>
              <select
                value={form.category}
                onChange={(e) =>
                  setForm({ ...form, category: e.target.value })
                }
                className="mt-1 w-full border p-2 rounded bg-white dark:bg-slate-700"
              >
                <option value="">Select category</option>
                <option value="PPT">PPT</option>
                <option value="Assignment">Assignment</option>
                <option value="Design">Design</option>
                <option value="Coding">Coding</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium dark:text-slate-200">
                Due Date
              </label>
              <input
                type="date"
                value={form.dueDate}
                onChange={(e) =>
                  setForm({ ...form, dueDate: e.target.value })
                }
                className="mt-1 w-full border p-2 rounded bg-white dark:bg-slate-700"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <input
              type="number"
              value={form.budgetMin}
              onChange={(e) =>
                setForm({ ...form, budgetMin: e.target.value })
              }
              placeholder="Min budget (₹)"
              className="border p-2 rounded bg-white dark:bg-slate-700"
            />

            <input
              type="number"
              value={form.budgetMax}
              onChange={(e) =>
                setForm({ ...form, budgetMax: e.target.value })
              }
              placeholder="Max budget (₹)"
              className="border p-2 rounded bg-white dark:bg-slate-700"
            />
          </div>

          <div>
            <label className="block text-sm font-medium dark:text-slate-200">
              Attach image (optional)
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleFile}
              className="mt-1"
            />
            {preview && (
              <img
                src={preview}
                alt="preview"
                className="mt-3 w-full h-48 object-cover rounded"
              />
            )}
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={loading}
              className={`px-4 py-2 rounded text-white ${
                loading
                  ? "bg-gray-400"
                  : "bg-indigo-600 hover:bg-indigo-700"
              }`}
            >
              {loading ? "Posting..." : "Post Task"}
            </button>

            <button
              type="button"
              onClick={() => navigate("/home")}
              className="px-4 py-2 rounded border"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
